import type { AdminSession } from "../types/common";

export const ADMIN_STORAGE_KEY = "dialin24_admin_user";
export const AUTH_CHANGE_EVENT = "dialin24-admin-auth-change";

type StoredAdminSession = Partial<AdminSession> | null;

const toTrimmedString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const normalizeEmail = (value: unknown) => toTrimmedString(value).toLowerCase();

export const normalizeStoredAdminSession = (
  adminUser: StoredAdminSession
): AdminSession | null => {
  if (!adminUser) {
    return null;
  }

  const token = toTrimmedString(adminUser.token);
  const email = normalizeEmail(adminUser.email);

  if (!token && !email) {
    return null;
  }

  return {
    token,
    tokenExpires: toTrimmedString(adminUser.tokenExpires) || undefined,
    name: toTrimmedString(adminUser.name) || "Admin User",
    email,
    role: toTrimmedString(adminUser.role) || "Admin",
    phone: toTrimmedString(adminUser.phone),
  };
};

export const getStoredAdminUser = (): AdminSession | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawAdminUser = window.localStorage.getItem(ADMIN_STORAGE_KEY);
    if (!rawAdminUser) {
      return null;
    }

    const parsedAdminUser = JSON.parse(rawAdminUser) as StoredAdminSession;
    return normalizeStoredAdminSession(parsedAdminUser);
  } catch {
    return null;
  }
};

export const getStoredAdminToken = (): string | null =>
  getStoredAdminUser()?.token || null;

const notifyAuthChange = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

export const saveStoredAdminUser = (adminUser: AdminSession) => {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedAdminUser = normalizeStoredAdminSession(adminUser);
  if (!normalizedAdminUser) {
    return;
  }

  window.localStorage.setItem(
    ADMIN_STORAGE_KEY,
    JSON.stringify(normalizedAdminUser)
  );
  notifyAuthChange();
};

export const clearStoredAdminUser = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(ADMIN_STORAGE_KEY);
  notifyAuthChange();
};

export const subscribeToAdminAuth = (callback: () => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleChange = () => callback();
  window.addEventListener(AUTH_CHANGE_EVENT, handleChange);
  window.addEventListener("storage", handleChange);

  return () => {
    window.removeEventListener(AUTH_CHANGE_EVENT, handleChange);
    window.removeEventListener("storage", handleChange);
  };
};